import {
  type ApercuImportDto,
  apercuSchema,
  type ConfirmationImportDto,
  confirmationSchema,
  type HistoriqueImportDto,
  historiqueSchema,
} from '@carte/contrats/import';
import { ErreurApi, obtenir } from '../../api';

export const CLE_HISTORIQUE_IMPORT = ['admin', 'import', 'historique'] as const;

/** Erreur de l'API d'import : le message français du serveur est conservé, il est affichable tel quel. */
export class ErreurImport extends ErreurApi {
  constructor(
    statut: number,
    code: string,
    public readonly detail: string | null,
    public readonly problemes: string[],
  ) {
    super(statut, code);
  }
}

async function lireReponse<T>(reponse: Response, analyser: (corps: unknown) => T): Promise<T> {
  const corps: unknown = await reponse.json().catch(() => ({}));
  if (!reponse.ok) {
    const c = (corps ?? {}) as { erreur?: unknown; message?: unknown; details?: unknown };
    const problemes = Array.isArray(c.details)
      ? c.details.flatMap((d) => {
          if (typeof d === 'string') return [d];
          const o = d as { liste?: unknown; id?: unknown; message?: unknown } | null;
          return o && typeof o.message === 'string'
            ? [`${o.liste ?? ''} ${o.id ?? ''} : ${o.message}`.trim()]
            : [];
        })
      : [];
    throw new ErreurImport(
      reponse.status,
      typeof c.erreur === 'string' ? c.erreur : 'erreur',
      typeof c.message === 'string' ? c.message : null,
      problemes,
    );
  }
  return analyser(corps);
}

/** `POST /api/import/apercu` : dépose le fichier, rien n'est écrit en base. */
export async function deposerFichier(fichier: File): Promise<ApercuImportDto> {
  const formulaire = new FormData();
  formulaire.append('fichier', fichier, fichier.name);
  const reponse = await fetch('/api/import/apercu', {
    method: 'POST',
    credentials: 'same-origin',
    body: formulaire,
  });
  return lireReponse(reponse, (c) => apercuSchema.parse(c));
}

/** `POST /api/import/:jeton/confirmer` : applique l'aperçu en une transaction. */
export async function confirmerImport(
  jeton: string,
  confirmationRenforcee: boolean,
): Promise<ConfirmationImportDto> {
  const reponse = await fetch(`/api/import/${encodeURIComponent(jeton)}/confirmer`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ confirmationRenforcee }),
  });
  return lireReponse(reponse, (c) => confirmationSchema.parse(c));
}

export function chargerHistoriqueImport(): Promise<HistoriqueImportDto> {
  return obtenir('/api/import/historique', historiqueSchema);
}

/** Message affichable en français pour une erreur de l'import. */
export function messageErreurImport(e: unknown): string {
  if (e instanceof ErreurApi) {
    if (e.statut === 401) return 'Votre session a expiré. Reconnectez-vous.';
    if (e.statut === 403) return 'Cet écran est réservé aux administrateurs.';
    if (e instanceof ErreurImport && e.detail) return e.detail;
    if (e.code === 'jeton_inconnu') return 'Cet aperçu a expiré. Redéposez le fichier pour en refaire un.';
    if (e.code === 'deja_importe') return 'Ce fichier a déjà été importé.';
    if (e.code === 'fichier_trop_gros') return 'Le fichier dépasse 10 Mo.';
  }
  return "Le serveur n'a pas pu répondre. Réessayez dans un instant.";
}
